import { Component, Input, EventEmitter, Output, ViewChild, OnInit, SimpleChanges, OnChanges, ElementRef } from '@angular/core';
import { ApiError, FormsUtilsService, MessageService } from '../../../../../core';
import { NgForm } from '@angular/forms';
import { CampanhaSelect, CampanhaSelectComponent } from '../select';
import { CampanhaCrudHttpService } from '../../services';
import { Campanha } from '../../campanha';
import { take } from 'rxjs/operators';

interface Config {
    isShowPossuiCampanha: boolean;
    isShowCampanha: boolean;
    isCampanhaRequired: boolean;
}

@Component({
    selector: 'sgt-campanha-select-form',
    templateUrl: './campanha-select-form.component.html',
    styleUrls: ['./campanha-select-form.component.less']
})
export class CampanhaSelectFormComponent implements OnInit, OnChanges {
    config: Config;

    @Input() editMode: boolean;
    @Input() canEdit = true;
    /** Se pode inserir uma nova campanha */
    @Input() canInsert = false;
    /** Emite evento para inserir uma nova campanha */
    @Output() insert = new EventEmitter<void>();
    @Input() canEditPossuiCampanha = true;
    @Input() required = false;
    @Input() possuiCampanha: boolean = null;
    @Output() possuiCampanhaChange = new EventEmitter<boolean>();
    @Input() idCampanha: number;
    @Output() idCampanhaChange = new EventEmitter<number>();
    @Input() campanha: Campanha;
    @Output() campanhaChange = new EventEmitter<Campanha>();

    // Filter
    /** Objetivo de venda */
    @Input() objetivoVenda: boolean = null;
    /** Objetivo de bonificação */
    @Input() objetivoBonificacao: boolean = null;
    /** Objetivo de aquisição */
    @Input() objetivoAquisicao: boolean = null;
    /** Cliente vinculado à campanha */
    @Input() idClienteVinculado: number = null;
    /** Representante vinculado à campanha */
    @Input() idRepresentante: number = null;
    /** Se deve filtrar somente campanhas diretamente vinculadas ao cliente */
    @Input() somenteClientesVinculados: boolean = null;

    // Configurações
    @Input() possuiCampanhaLabel = 'Possui campanha?';
    @Input() possuiCampanhaHelpText = 'Selecione se possui campannha ou não';
    @Input() possuiCampanhaMsgError = 'É obrigatório informar se possui campanha';
    @Input() campanhaLabelInsert = 'Campanha';
    @Input() campanhaLabelEdit = 'Campanha';
    @Input() campanhaHelpTextInsert = 'Selecione a campanha';
    @Input() campanhaHelpTextEdit = 'Campanha';
    @Input() campanhaHelpMsgError = 'É obrigatório selecionar a campanha';

    @ViewChild('form') form: NgForm;
    @ViewChild('possuiCampanhaInput') possuiCampanhaInput: ElementRef;
    @ViewChild('campanhaSelect') campanhaSelect: CampanhaSelectComponent;

    constructor(private messageService: MessageService,
                private formsUtilsService: FormsUtilsService,
                private campanhaCrudHttpService: CampanhaCrudHttpService) {}

    ngOnInit(): void {
        this.setConfig();
    }

    ngOnChanges(changes: SimpleChanges): void {
        for (const propName in changes) {
            if (propName === 'possuiCampanha') {
                this.onChangePossuiCampanha(this.possuiCampanha);
            }
        }
    }

    private setConfig() {
        this.config = {
            isShowPossuiCampanha: !this.editMode,
            isShowCampanha: this.editMode || this.possuiCampanha,
            isCampanhaRequired: this.required || this.possuiCampanha
        };
    }

    /** Seta foco no input de se possui campanha */
    setFocusPossuiCampanha() {
        jQuery(this.possuiCampanhaInput.nativeElement).trigger('focus');
    }

    /** Seta foco no select da campanha */
    setFocusCampanha() {
        this.campanhaSelect.setFocus();
    }

    /** Ao alterar a flag se possui campanha */
    onChangePossuiCampanha(possuiCampanha: boolean) {
        if (!this.config) {
            return;
        }

        this.possuiCampanha = possuiCampanha;
        this.possuiCampanhaChange.emit(this.possuiCampanha);
        this.config.isShowCampanha = this.editMode || this.possuiCampanha;
        this.config.isCampanhaRequired = this.required || this.possuiCampanha;
    }

    /** Ao alterar acampanha */
    async onChangeCampanha(idCampanha: number) {
        try {
            // Setando o ID da campanha
            this.idCampanha = idCampanha;
            this.idCampanhaChange.emit(this.idCampanha);

            if (this.idCampanha) {
                // Carregando a campanha selecionada
                this.campanha = await this.findCampanha();
            } else {
                this.campanha = null;
            }

            this.campanhaChange.emit(this.campanha);
        } catch (error) {
            this.messageService.error(error.cause, error.message, error);
            this.campanha = null;
            this.campanhaChange.emit(this.campanha);
        }
    }

    /** Carrega a campanha selecionada */
    private findCampanha(): Promise<Campanha> {
        return new Promise<Campanha>((resolve, reject) => {
            this.campanhaCrudHttpService.find(this.idCampanha)
                .pipe(
                    take(1)
                )
                .subscribe(
                    campanha => resolve(campanha),
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Adiciona a campanha informada ao select */
    addCampanha(campanha: CampanhaSelect) {
        this.campanhaSelect?.addCampanha(campanha);
    }

    isValid(): boolean {
        this.formsUtilsService.makeAllFieldsDirty(this.form);

        // Sem a flag se possui campanha informada
        if (this.config.isShowPossuiCampanha && (this.possuiCampanha == null || this.possuiCampanha === undefined)) {
            this.messageService.warning(this.possuiCampanhaMsgError, 'Atenção');
            setTimeout(() => this.setFocusPossuiCampanha(), 300);
            return false;
        }

        // Sem a campanha selecionada
        if ((this.possuiCampanha || this.required) && !this.idCampanha) {
            this.messageService.warning(this.campanhaHelpMsgError, 'Atenção');
            setTimeout(() => this.setFocusCampanha(), 300);
            return false;
        }

        // Form inválido
        if (this.form.invalid) {
            this.messageService.info('Dados de campanha inválidos. Verifique a campanha', 'Informação');
            return false;
        }

        return true;
    }

    resolve() {
        // Se não possui campanha
        if (!this.editMode && !this.possuiCampanha) {
            this.onChangeCampanha(null);
        }
    }
}
